/** Labels and product-image loader for Konva hosts — kept off the stage. */
import { useMemo } from "react";

import { useTranslation } from "src/i18n";
import { urls } from "src/utils/urls";
import { getPegboardFacingChangeLabelKey } from "../components/Pegboards/pegboardFacingChange";
import type { PlanogramAssetProvider, PlanogramRendererLabels } from "../components/KonvaPlanogram";
import type { PlanogramFixtureType } from "../components/KonvaPlanogram/types";

export const createPlanogramAssetProvider = (
  merchantId: number | null,
): PlanogramAssetProvider | undefined => {
  if (merchantId === null) return undefined;

  return {
    loadProductImage: ({ productCode }) =>
      new Promise((resolve) => {
        if (typeof window === "undefined") {
          resolve(null);
          return;
        }

        const image = new window.Image();
        // Do not set crossOrigin: that turns this into a canvas-read CORS request.
        image.onload = () => resolve({ image });
        image.onerror = () => resolve(null);
        image.src = urls.productImage(productCode, merchantId, "small");
      }),
  };
};

export const usePlanogramRendererLabels = (): PlanogramRendererLabels => {
  const { t } = useTranslation("common");

  return useMemo<PlanogramRendererLabels>(() => {
    const fixtureName = (fixtureType: PlanogramFixtureType) =>
      t(
        fixtureType === "RISER"
          ? "cdtPriorityOrder.riserFixture"
          : "cdtPriorityOrder.hangcellFixture",
      );

    return {
      bay: (bayNo) => t("cdtPriorityOrder.bayLabel", { bayNo }),
      containers: t("cdtPriorityOrder.planogramContainers"),
      fixture: (fixtureType, shelfNo) =>
        t("cdtPriorityOrder.fixture", { fixtureName: fixtureName(fixtureType), shelfNo }),
      fixtures: t("cdtPriorityOrder.planogramFixtures"),
      newItemIndicator: t("planogramCanvas.newItemIndicator"),
      pegboardFacingChangeIndicator: (changeType) => {
        const labelKey = getPegboardFacingChangeLabelKey(changeType);
        return labelKey ? t("pegboardFacingChange.indicator", { changeType: t(labelKey) }) : "";
      },
      moveLeft: t("cdtPriorityOrder.moveLeft"),
      moveProductLeft: (label) => t("cdtPriorityOrder.moveProductLeft", { productName: label }),
      moveProductRight: (label) => t("cdtPriorityOrder.moveProductRight", { productName: label }),
      moveRight: t("cdtPriorityOrder.moveRight"),
      pegboards: t("cdtPriorityOrder.planogramPegboards"),
      placements: t("cdtPriorityOrder.planogramPlacements"),
      products: t("cdtPriorityOrder.planogramProducts"),
      reset: t("cdtPriorityOrder.reset"),
      resetViewport: t("cdtPriorityOrder.resetViewport"),
      resizeFixtureNarrower: (fixtureType, shelfNo) =>
        t("cdtPriorityOrder.resizeFixtureNarrower", {
          fixtureName: fixtureName(fixtureType),
          shelfNo,
        }),
      resizeFixtureNarrowerText: t("cdtPriorityOrder.resizeFixtureNarrowerText"),
      resizeFixtureWider: (fixtureType, shelfNo) =>
        t("cdtPriorityOrder.resizeFixtureWider", {
          fixtureName: fixtureName(fixtureType),
          shelfNo,
        }),
      resizeFixtureWiderText: t("cdtPriorityOrder.resizeFixtureWiderText"),
      resizeShelfNarrower: (shelfNo) => t("cdtPriorityOrder.resizeShelfNarrower", { shelfNo }),
      resizeShelfNarrowerText: t("cdtPriorityOrder.resizeShelfNarrowerText"),
      resizeShelfWider: (shelfNo) => t("cdtPriorityOrder.resizeShelfWider", { shelfNo }),
      resizeShelfWiderText: t("cdtPriorityOrder.resizeShelfWiderText"),
      selectContainer: (label) => t("cdtPriorityOrder.selectContainer", { label }),
      selectFixture: (fixtureType, shelfNo) =>
        t("cdtPriorityOrder.selectFixture", {
          fixtureName: fixtureName(fixtureType),
          shelfNo,
        }),
      selectPegboard: (id) => t("cdtPriorityOrder.selectPegboard", { id }),
      selectPlacement: (label) => t("cdtPriorityOrder.selectPlacement", { label }),
      selectProduct: (label) => t("cdtPriorityOrder.selectProduct", { productName: label }),
      selectShelf: (shelfNo) => t("cdtPriorityOrder.selectShelf", { shelfNo }),
      shelf: (shelfNo) => t("cdtPriorityOrder.shelf", { shelfNo }),
      shelves: t("cdtPriorityOrder.planogramShelves"),
      viewportControls: t("cdtPriorityOrder.viewportControls"),
      zoomIn: t("cdtPriorityOrder.zoomIn"),
      zoomOut: t("cdtPriorityOrder.zoomOut"),
    };
  }, [t]);
};
